import { Component } from '@theme/component';

const desktopQuery = window.matchMedia('(min-width: 1024px)');
const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
const supportsInvokerCommands = 'command' in HTMLButtonElement.prototype;

const WISHLIST_KEY = 'tph-wishlist';
const WISHLIST_EVENT = 'tph:wishlist-change';

const CAR_ICON =
  '<svg class="tph-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 15.5v-3l2-4.8A2 2 0 0 1 6.8 6.5h10.4a2 2 0 0 1 1.8 1.2l2 4.8v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/><path d="M3.5 12h17"/><circle cx="7.5" cy="16.5" r="1.8"/><circle cx="16.5" cy="16.5" r="1.8"/></svg>';

/**
 * @param {unknown} value
 * @returns {string}
 */
const normalize = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Header for the groofoo design: dropdowns, mobile drawer, mobile search row, scroll state and the
 * --header-height / --header-group-height variables other sticky theme elements read.
 *
 * @typedef {object} HeaderRefs
 * @property {HTMLButtonElement} [menuButton]
 * @property {HTMLDialogElement} [drawer]
 * @property {HTMLAnchorElement} [searchToggle]
 * @property {HTMLElement} [mobileSearch]
 * @property {HTMLDetailsElement[]} [dropdowns]
 *
 * @extends {Component<HeaderRefs>}
 */
class TphHeaderComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {number | undefined} */
  #hoverTimer;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('keydown', this.#onDocumentKeydown, { signal });
    // Desktop scrolls inside .page-wrapper rather than the window; a capturing listener hears either
    document.addEventListener('scroll', this.#onScroll, { passive: true, capture: true, signal });
    desktopQuery.addEventListener('change', this.#onBreakpointChange, { signal });

    const { searchToggle } = this.refs;
    if (searchToggle) {
      searchToggle.setAttribute('role', 'button');
      searchToggle.setAttribute('aria-expanded', 'false');
    }

    this.#observeHeights();
    this.#onScroll();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeHeights();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    window.clearTimeout(this.#hoverTimer);
    if (this.refs.drawer?.open) this.refs.drawer.close();
  }

  /* ---------- Drawer ---------- */

  /**
   * Opens the mobile menu. Browsers with invoker commands open it from the button's `command`
   * attribute as well; showModal() is a no-op on an already open modal dialog.
   * @param {MouseEvent} [event]
   */
  openDrawer(event) {
    const { drawer } = this.refs;
    if (!drawer) return;
    if (event && supportsInvokerCommands) event.preventDefault();

    this.#toggleMobileSearch(false);
    if (!drawer.open) drawer.showModal();
  }

  /**
   * @param {MouseEvent} [event]
   */
  closeDrawer(event) {
    if (event && supportsInvokerCommands) event.preventDefault();
    this.refs.drawer?.close();
  }

  /**
   * Closes the drawer on a backdrop click (the dialog itself) or when a link inside it is followed.
   * @param {MouseEvent} event
   */
  onDrawerClick(event) {
    const { drawer } = this.refs;
    const origin = event.composedPath()[0];
    if (!drawer || !(origin instanceof Element)) return;

    if (origin === drawer || origin.closest('a[href]')) drawer.close();
  }

  /* ---------- Dropdowns ---------- */

  /**
   * Keeps a single dropdown open.
   * @param {Event} event
   */
  onDropdownToggle(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    this.#closeDropdowns(details);
  }

  /**
   * @param {PointerEvent} event
   */
  onDropdownEnter(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !hoverQuery.matches || !desktopQuery.matches) return;
    window.clearTimeout(this.#hoverTimer);
    details.open = true;
  }

  /**
   * @param {PointerEvent} event
   */
  onDropdownLeave(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !hoverQuery.matches || !desktopQuery.matches) return;
    window.clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => {
      details.open = false;
    }, 160);
  }

  /* ---------- Vehicle mega menu ---------- */

  /**
   * Switches the vehicle menu between car and bike covers. CSS shows the matching list and card image.
   * @param {MouseEvent} event
   */
  onMegaTab(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-mega-tab]') : null;
    const mega = button?.closest('.tph-mega');
    if (!(button instanceof HTMLElement) || !(mega instanceof HTMLElement)) return;

    const tab = button.dataset.megaTab === 'bike' ? 'bike' : 'car';
    mega.dataset.tab = tab;
    for (const other of mega.querySelectorAll('[data-mega-tab]')) {
      if (other instanceof HTMLElement) other.setAttribute('aria-pressed', String(other.dataset.megaTab === tab));
    }
  }

  /**
   * @param {HTMLDetailsElement} [except]
   */
  #closeDropdowns(except) {
    for (const details of this.refs.dropdowns ?? []) {
      if (details !== except) details.open = false;
    }
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;
    for (const details of this.refs.dropdowns ?? []) {
      if (details.open && !details.contains(target)) details.open = false;
    }
  };

  /** @param {KeyboardEvent} event */
  #onDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const open = (this.refs.dropdowns ?? []).find((details) => details.open);
    if (!open) return;
    open.open = false;
    open.querySelector('summary')?.focus();
  };

  /* ---------- Mobile search ---------- */

  /**
   * Shows or hides the search row on small screens. Without JavaScript the toggle links to the search page.
   * @param {MouseEvent} event
   */
  toggleMobileSearch(event) {
    if (!this.refs.mobileSearch) return;
    event.preventDefault();
    this.#toggleMobileSearch(this.refs.mobileSearch.hidden);
  }

  /**
   * @param {boolean} open
   */
  #toggleMobileSearch(open) {
    const { mobileSearch, searchToggle } = this.refs;
    if (!mobileSearch || !searchToggle) return;

    mobileSearch.hidden = !open;
    searchToggle.setAttribute('aria-expanded', String(open));
    if (open) mobileSearch.querySelector('input[type="search"]')?.focus();
    this.#updateHeights();
  }

  /* ---------- Layout state ---------- */

  #onBreakpointChange = () => {
    this.#closeDropdowns();
    if (!desktopQuery.matches) return;
    this.refs.drawer?.close();
    this.#toggleMobileSearch(false);
  };

  #onScroll = () => {
    const pageScroll = document.querySelector('.page-wrapper')?.scrollTop ?? 0;
    this.toggleAttribute('data-scrolled', window.scrollY > 4 || pageScroll > 4);
  };

  #observeHeights() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => this.#updateHeights());

    const group = document.getElementById('header-group');
    for (const element of group?.children ?? [this]) this.#resizeObserver.observe(element);
    this.#updateHeights();
  }

  #updateHeights() {
    const group = document.getElementById('header-group');
    let groupHeight = 0;
    for (const element of group?.children ?? []) {
      if (element instanceof HTMLElement) groupHeight += element.offsetHeight;
    }

    document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);
    // A transparent header floats over the hero; the hero and the header wrapper read this to make room
    document.body.style.setProperty('--tph-header-offset', `${this.offsetHeight}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight || this.offsetHeight}px`);
  }
}

/**
 * Search field with suggestions: vehicles from Theme settings > Vehicle finder first, then products
 * from Shopify predictive search. Labels come from data attributes rendered with translations.
 *
 * @typedef {object} SearchRefs
 * @property {HTMLInputElement} input
 * @property {HTMLElement} results
 * @property {HTMLElement} status
 *
 * @typedef {{ make: string, model: string, url: string }} Vehicle
 * @typedef {{ title: string, url: string, image?: string }} ProductSuggestion
 *
 * @extends {Component<SearchRefs>}
 */
class TphSearchComponent extends Component {
  requiredRefs = ['input', 'results', 'status'];

  /** @type {Vehicle[]} */
  #vehicles = [];
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #requestController = null;
  /** @type {number | undefined} */
  #debounceTimer;
  #activeIndex = -1;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    document.addEventListener('click', this.#onDocumentClick, { signal: this.#abortController.signal });
    this.#vehicles = this.#readVehicles();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#requestController?.abort();
    window.clearTimeout(this.#debounceTimer);
  }

  /** @returns {Vehicle[]} */
  #readVehicles() {
    const source = this.dataset.vehiclesSource ? document.getElementById(this.dataset.vehiclesSource) : null;
    if (!source) return [];
    try {
      return JSON.parse(source.textContent ?? '{}').vehicles ?? [];
    } catch (error) {
      console.warn('[tph-search] Could not read vehicle data', error);
      return [];
    }
  }

  onInput() {
    window.clearTimeout(this.#debounceTimer);
    this.#debounceTimer = window.setTimeout(() => this.#update(), 180);
  }

  onFocus() {
    if (this.refs.input.value.trim().length >= 2 && this.refs.results.childElementCount) this.#open();
  }

  /**
   * @param {SubmitEvent} event
   */
  onSubmit(event) {
    if (this.refs.input.value.trim()) return;
    event.preventDefault();
    this.refs.input.focus();
  }

  /**
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    if (event.key === 'Escape') {
      this.#close();
      return;
    }
    if (this.refs.results.hidden) return;

    const options = this.#options;
    if (!options.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.#setActive((this.#activeIndex + step + options.length) % options.length);
    } else if (event.key === 'Enter' && this.#activeIndex >= 0) {
      event.preventDefault();
      options[this.#activeIndex]?.click();
    }
  }

  get #options() {
    return [...this.refs.results.querySelectorAll('[role="option"]')];
  }

  /**
   * @param {number} index
   */
  #setActive(index) {
    const options = this.#options;
    this.#activeIndex = index;
    options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));

    const active = options[index];
    if (active) {
      this.refs.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      this.refs.input.removeAttribute('aria-activedescendant');
    }
  }

  /**
   * @param {string} query
   * @returns {Vehicle[]}
   */
  #matchVehicles(query) {
    const tokens = normalize(query).split(' ').filter(Boolean);
    if (!tokens.length) return [];
    return this.#vehicles
      .filter((vehicle) => {
        const haystack = normalize(`${vehicle.make} ${vehicle.model}`);
        return tokens.every((token) => haystack.includes(token));
      })
      .slice(0, 6);
  }

  /**
   * @param {string} query
   * @returns {Promise<ProductSuggestion[] | null>} null when superseded by a newer request
   */
  async #fetchProducts(query) {
    this.#requestController?.abort();
    this.#requestController = new AbortController();

    const url = new URL(`${this.dataset.suggestUrl ?? '/search/suggest'}.json`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(url, { signal: this.#requestController.signal });
      if (!response.ok) return [];
      const data = await response.json();
      return data?.resources?.results?.products ?? [];
    } catch (error) {
      return error instanceof DOMException && error.name === 'AbortError' ? null : [];
    }
  }

  async #update() {
    const query = this.refs.input.value.trim();
    if (query.length < 2) {
      this.#requestController?.abort();
      this.refs.results.replaceChildren();
      this.#close();
      return;
    }

    const vehicles = this.#matchVehicles(query);
    this.#render(query, vehicles, [], true);

    const products = await this.#fetchProducts(query);
    if (products === null || query !== this.refs.input.value.trim()) return;

    const vehiclePaths = new Set(vehicles.map((vehicle) => new URL(vehicle.url, window.location.origin).pathname));
    const extraProducts = products.filter(
      (product) => !vehiclePaths.has(new URL(product.url, window.location.origin).pathname)
    );
    this.#render(query, vehicles, extraProducts, false);
    this.refs.status.textContent = (this.dataset.labelCount ?? '').replace(
      '[count]',
      String(vehicles.length + extraProducts.length)
    );
  }

  /**
   * @param {string} query
   * @param {Vehicle[]} vehicles
   * @param {ProductSuggestion[]} products
   * @param {boolean} loading
   */
  #render(query, vehicles, products, loading) {
    const fragment = document.createDocumentFragment();
    const idPrefix = this.refs.results.id;
    let index = 0;

    /** @param {string} text */
    const addLabel = (text) => {
      const label = document.createElement('p');
      label.className = 'tph-search__label';
      label.setAttribute('aria-hidden', 'true');
      label.textContent = text;
      fragment.append(label);
    };

    /**
     * @param {string} href
     * @param {string} className
     */
    const createOption = (href, className) => {
      const link = document.createElement('a');
      link.className = className;
      link.href = href;
      link.id = `${idPrefix}-option-${index++}`;
      link.tabIndex = -1;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      return link;
    };

    if (vehicles.length) {
      addLabel(this.dataset.labelVehicles ?? '');
      for (const vehicle of vehicles) {
        const link = createOption(vehicle.url, 'tph-search__result');
        link.insertAdjacentHTML('afterbegin', CAR_ICON);
        const text = document.createElement('span');
        text.textContent = `${vehicle.make} ${vehicle.model}`;
        const hint = document.createElement('small');
        hint.textContent = this.dataset.labelViewCover ?? '';
        text.append(hint);
        link.append(text);
        fragment.append(link);
      }
    }

    if (products.length) {
      addLabel(this.dataset.labelProducts ?? '');
      for (const product of products) {
        const link = createOption(product.url, 'tph-search__result');
        if (product.image) {
          const image = document.createElement('img');
          const imageUrl = new URL(product.image, window.location.origin);
          imageUrl.searchParams.set('width', '96');
          image.src = imageUrl.toString();
          image.alt = '';
          image.width = 42;
          image.height = 42;
          image.loading = 'lazy';
          link.append(image);
        }
        const text = document.createElement('span');
        text.textContent = product.title;
        link.append(text);
        fragment.append(link);
      }
    }

    if (loading && !vehicles.length) {
      const empty = document.createElement('p');
      empty.className = 'tph-search__empty';
      empty.textContent = this.dataset.labelLoading ?? '';
      fragment.append(empty);
    }

    const form = this.querySelector('form');
    const searchUrl = new URL(form?.getAttribute('action') ?? '/search', window.location.origin);
    searchUrl.searchParams.set('q', query);
    searchUrl.searchParams.set('type', 'product');
    searchUrl.searchParams.set('options[prefix]', 'last');
    const all = createOption(searchUrl.pathname + searchUrl.search, 'tph-search__all');
    all.textContent = (this.dataset.labelSearchFor ?? '').replace('[terms]', query);
    fragment.append(all);

    this.refs.results.replaceChildren(fragment);
    this.#setActive(-1);
    this.#open();
  }

  #open() {
    this.refs.results.hidden = false;
    this.refs.input.setAttribute('aria-expanded', 'true');
  }

  #close() {
    this.refs.results.hidden = true;
    this.refs.input.setAttribute('aria-expanded', 'false');
    this.#setActive(-1);
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#close();
  };
}

/**
 * Wishlist heart in the header: shows how many products the shopper has hearted. The hearts are saved in this
 * browser by assets/tph-bestsellers.js, which announces every change with the tph:wishlist-change event.
 *
 * @typedef {object} WishlistRefs
 * @property {HTMLAnchorElement} link
 * @property {HTMLElement} count
 *
 * @extends {Component<WishlistRefs>}
 */
class TphWishlistComponent extends Component {
  requiredRefs = ['link', 'count'];

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    window.addEventListener('storage', this.#update, { signal });
    window.addEventListener(WISHLIST_EVENT, this.#update, { signal });
    this.#update();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @returns {number} */
  #readCount() {
    try {
      const saved = JSON.parse(localStorage.getItem(WISHLIST_KEY) ?? '[]');
      return Array.isArray(saved) ? saved.length : 0;
    } catch {
      return 0;
    }
  }

  #update = () => {
    const { link, count } = this.refs;
    const total = this.#readCount();

    count.hidden = total === 0;
    count.textContent = total > 99 ? '99+' : String(total);
    const label = total > 0 ? (this.dataset.labelCount ?? '').replace('[count]', String(total)) : this.dataset.label;
    link.setAttribute('aria-label', label ?? '');
  };
}

if (!customElements.get('tph-header-component')) customElements.define('tph-header-component', TphHeaderComponent);
if (!customElements.get('tph-search-component')) customElements.define('tph-search-component', TphSearchComponent);
if (!customElements.get('tph-wishlist-component')) customElements.define('tph-wishlist-component', TphWishlistComponent);
